/**
 * LibraryPackageCell — the Package column for a tracker the library serves
 * through badge → select → confirm (instant apply); "No package"
 * unassigns, config values untouched. Deleted packages never dangle (FK
 * SetNull clears assignments server-side). State and writes live in
 * useLibraryPackageCell.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Package } from 'lucide-react';
import type { FlowOptimizerLibraryProvider } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';

import { PackageBadge } from './PackageBadge';
import { PackageEditor } from './PackageEditor';
import { useLibraryPackageCell } from './useLibraryPackageCell';

export interface LibraryPackageCellProps {
  row: OptimizerRow;
  /** The row's tracker, narrowed by libraryProviderOf. */
  provider: FlowOptimizerLibraryProvider;
  canEdit: boolean;
}

export const LibraryPackageCell: FC<LibraryPackageCellProps> = ({ row, provider, canEdit }) => {
  const cell = useLibraryPackageCell({ row, provider });
  if (cell.editing) return <PackageEditor row={row} {...cell.editor} />;
  if (row.package) {
    return <PackageBadge pkg={row.package} onEdit={canEdit ? cell.open : undefined} />;
  }
  if (!canEdit) return <span className="text-muted-foreground">—</span>;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
      onClick={cell.open}
    >
      <Package className="h-3 w-3" aria-hidden /> + Package
    </Button>
  );
};

export default LibraryPackageCell;
